import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShoppingCart, ClipboardCheck, ArrowLeftRight, Inbox, Wifi, WifiOff, RefreshCw, Settings, ChevronRight, ChevronLeft, Unlink, Search, X, Loader2, Printer, FileText, ExternalLink, CheckCircle2, ScanLine, FolderUp, Check, History, AlertTriangle, Wallet, User } from 'lucide-react';
import { toast } from 'sonner';
import TerminalSales from './TerminalSales';
import TerminalPOCheck from './TerminalPOCheck';
import TerminalTransfers from './TerminalTransfers';
import TerminalDocUpload from './TerminalDocUpload';
import TerminalHistoryOverlay from '../../components/TerminalHistoryOverlay';
import TerminalCollectPayment from './TerminalCollectPayment';
import TerminalCustomerAccount from './TerminalCustomerAccount';
import axios from 'axios';
import PrintEngine from '../../lib/PrintEngine';
import PrintBridge from '../../lib/PrintBridge';
import SendToPrintModal from '../../components/SendToPrintModal';
import { DeviceIdentity } from '../../plugins/DeviceIdentityPlugin';
import {
  cacheProducts, getProducts, cacheCustomers,
  cachePriceSchemes, cacheInventory,
  cacheBranchPrices, setOfflineOrg, getPendingSaleCount,
  getProductCount, getLastSyncTime, setLastSyncTime,
  mergeProducts, mergeCustomers, updateInventoryBatch,
  getMeta, setOfflineAdminPinHash, setOfflinePinGrants,
  saveBusinessInfoCache, getCachedBusinessInfo, urlToBase64,
} from '../../lib/offlineDB';
import { syncPendingSales, startAutoSync, stopAutoSync } from '../../lib/syncManager';
import {
  getStoredDevice, storeDevice, wipeDevice, getAutoPrint, setAutoPrint,
  ledgerHas, ledgerAdd, generatePairingCode, pollPairing, heartbeat as deviceHeartbeat,
  claimJobs, jobDone, jobFailed, jobProgress, fetchRenderData,
} from './printDevice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace(/^http/, 'ws');
const fmtPHP = (v) => `₱${(parseFloat(v) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;

// Transform basic doc data (from /api/doc/view/:code) into PrintEngine-compatible format
function basicDocToPrintData(basic) {
  if (!basic) return {};
  if (basic.doc_type === 'invoice') {
    return {
      invoice_number: basic.number,
      customer_name: basic.customer_name,
      order_date: basic.order_date || basic.date,
      created_at: basic.date,
      items: (basic.items || []).map(i => ({
        product_name: i.name,
        quantity: i.qty,
        rate: i.price,
        total: i.total,
        // Per-line discount carried from /api/doc/view — was previously
        // hardcoded to 0 here, which silently dropped line discounts from
        // the terminal reprint even when the totals were right.
        discount_amount: parseFloat(i.discount_amount || 0) || 0,
        discount_value: parseFloat(i.discount_value || 0) || 0,
        discount_type: i.discount_type || 'amount',
      })),
      subtotal: basic.subtotal,
      overall_discount: basic.discount || 0,
      grand_total: basic.grand_total,
      amount_paid: basic.amount_paid,
      balance: basic.balance,
      payment_method: basic.payment_method,
      payment_type: basic.payment_type,
    };
  }
  if (basic.doc_type === 'purchase_order') {
    return {
      po_number: basic.number,
      purchase_date: basic.date,
      vendor: basic.supplier_name,
      status: basic.raw_status || basic.status,
      items: (basic.items || []).map(i => ({
        product_name: i.name, quantity: i.qty, unit_price: i.price, total: i.total,
      })),
      subtotal: basic.grand_total,
      grand_total: basic.grand_total,
      payment_status: basic.payment_status,
    };
  }
  if (basic.doc_type === 'branch_transfer') {
    return {
      order_number: basic.number,
      created_at: basic.date,
      from_branch_name: basic.from_branch,
      to_branch_name: basic.to_branch,
      status: basic.raw_status || basic.status,
      items: (basic.items || []).map(i => ({
        product_name: i.name, qty: i.qty, transfer_capital: i.price, branch_retail: 0,
      })),
    };
  }
  if (basic.doc_type === 'stock_request') {
    return {
      request_number: basic.number,
      created_at: basic.date,
      sent_at: basic.sent_at,
      status: basic.raw_status || basic.status,
      requesting_branch_name: basic.requesting_branch_name,
      supplying_branch_name: basic.supplying_branch_name,
      created_by_name: basic.created_by_name,
      triaged_at: basic.triaged_at,
      triaged_by: basic.triaged_by,
      notes: basic.notes,
      items: (basic.items || []).map(i => ({
        product_name: i.name, qty: i.qty, unit: i.unit, status: i.line_status,
      })),
    };
  }
  return basic;
}

const QUICKSCAN_DOC_LABELS = {
  invoice: 'Sales Receipt', purchase_order: 'Purchase Order',
  branch_transfer: 'Branch Transfer', stock_request: 'Stock Request',
};
const QUICKSCAN_PRINT_TYPE = {
  invoice: 'order_slip', purchase_order: 'purchase_order',
  branch_transfer: 'branch_transfer', stock_request: 'stock_request',
};

// Format relative time: "Just now", "2 min ago", "1 hr ago", etc.
function formatRelativeTime(isoString) {
  if (!isoString) return 'Never';
  const diff = Date.now() - new Date(isoString).getTime();
  if (diff < 0) return 'Just now';
  const secs = Math.floor(diff / 1000);
  if (secs < 30) return 'Just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}


const TABS = [
  { key: 'sales', label: 'Sales', icon: ShoppingCart, color: 'text-emerald-600 bg-emerald-50' },
  { key: 'po', label: 'PO Check', icon: ClipboardCheck, color: 'text-amber-600 bg-amber-50' },
  { key: 'transfers', label: 'Transfers', icon: ArrowLeftRight, color: 'text-blue-600 bg-blue-50' },
];

export default function TerminalShell({ session, onLogout, onSessionUpdate }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Auto-open Scan QR overlay when redirected back from DocViewerPage (?scan=1)
  useEffect(() => {
    if (searchParams.get('scan') === '1') {
      startQrScanner();
      navigate('/terminal', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [activeTab, setActiveTab] = useState('sales');
  const [modeMenuOpen, setModeMenuOpen] = useState(false);
  // Tablet side-rail collapse — default collapsed (icon-only) so most of the
  // screen stays available for the Sales product/cart grid. Persisted in
  // localStorage so the cashier's choice survives reloads.
  const [railExpanded, setRailExpanded] = useState(() => {
    try { return localStorage.getItem('terminal_rail_expanded') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('terminal_rail_expanded', railExpanded ? '1' : '0'); } catch {}
  }, [railExpanded]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const [docCodeInput, setDocCodeInput] = useState('');
  const [showDocSearch, setShowDocSearch] = useState(false);
  const [docSearchResults, setDocSearchResults] = useState([]);
  const [docSearchLoading, setDocSearchLoading] = useState(false);
  // Quick scan sheet — shown when hardware scanner reads a doc QR code
  const [quickScanDoc, setQuickScanDoc] = useState(null);  // { basic, code, loading }
  const [businessInfo, setBusinessInfo] = useState({});
  const [pendingCount, setPendingCount] = useState(0);
  const [syncProgress, setSyncProgress] = useState('');
  const [backgroundSyncStatus, setBackgroundSyncStatus] = useState(''); // '', 'syncing', 'done', 'error'
  const [syncVersion, setSyncVersion] = useState(0); // Incremented after background sync to trigger TerminalSales re-read
  const [lastSyncDisplay, setLastSyncDisplay] = useState(''); // "2 min ago", "Just now", etc.
  const [notifications, setNotifications] = useState([]);
  // Phase D — Multi-Cashier-Shifts surface on terminal (only when feature ON)
  const [shiftStatus, setShiftStatus] = useState({
    multiShiftEnabled: !!session.multiShiftEnabled,
    requiresShiftOpen: !!session.requiresShiftOpen,
    activeShift: session.activeShift || null,
  });
  const wsRef = useRef(null);
  const poRefreshRef = useRef(null); // callback to refresh PO list
  const transferRefreshRef = useRef(null);
  // Global hardware scanner buffer (for H10P Newland HID keyboard wedge)
  const globalScanBufferRef = useRef('');
  const globalScanTimerRef = useRef(null);
  // QR Camera Scanner state
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const qrScannerOpenRef = useRef(false);   // mirrors state — readable by stale closures
  const qrScannerRef = useRef(null);
  const qrLastScanRef = useRef({ code: '', time: 0 });
  const QR_SCAN_COOLDOWN = 3000;
  // Keep ref in sync so the stale globalScanKey closure can read it
  useEffect(() => { qrScannerOpenRef.current = qrScannerOpen; }, [qrScannerOpen]);

  // Token ref — always holds the latest token for axios interceptor
  const tokenRef = useRef(session.token);

  // Document Upload overlay state
  const [docUploadOpen, setDocUploadOpen] = useState(false);

  // Collect Payment overlay state
  const [collectPaymentOpen, setCollectPaymentOpen] = useState(false);
  // Customer Account overlay state
  const [customerAccountOpen, setCustomerAccountOpen] = useState(false);
  const [customerAccountPrefill, setCustomerAccountPrefill] = useState(null);

  // Print Terminal state — print-device claim contract (see printDevice.js
  // + backend/routes/print_receivers.py). Auto Print is terminal-local.
  const [printDevice, setPrintDevice] = useState(() => getStoredDevice()); // {token, name, branch_name} | null
  const [printMode, setPrintMode] = useState(() => (getAutoPrint() ? 'auto' : 'manual')); // 'auto' | 'manual'
  const [pairing, setPairing] = useState(null); // {code, status: 'pending'|'expired'} while a code is displayed
  const [claimPaused, setClaimPaused] = useState(''); // 403 detail — claiming stopped until Retry
  const [printQueue, setPrintQueue] = useState([]); // claimed jobs awaiting manual Print
  const [printQueueOpen, setPrintQueueOpen] = useState(false);
  const [quickScanCloudPrint, setQuickScanCloudPrint] = useState(false); // cloud print for scanned doc
  const printingRef = useRef(false); // pauses the claim loop while a print is in flight
  const printModeRef = useRef(printMode);
  useEffect(() => { printModeRef.current = printMode; }, [printMode]);
  const printDeviceRef = useRef(printDevice);
  useEffect(() => { printDeviceRef.current = printDevice; }, [printDevice]);

  // ── POS History overlay (Sales History + Purchase History sheet)
  // Single sheet behind a PIN gate. Branch scope is locked to the
  // terminal's pairing (session.branchId). Server enforces:
  //   • Owner / Org Admin PIN → any branch
  //   • TOTP (Authenticator)  → any branch
  //   • Manager PIN           → ONLY if their assigned branch matches
  // See POSHistoryUnlockGate + verify_pin_for_action('pos_view_history').
  const [historyOverlayOpen, setHistoryOverlayOpen] = useState(false);
  const [posHistoryPinSession, setPosHistoryPinSession] = useState(null);
  const startHistoryPinSession = useCallback((pin, method, name) => {
    setPosHistoryPinSession({ pin: String(pin || ''), method, name: name || '', at: Date.now() });
  }, []);
  // Resets the idle timer — called on any user interaction inside the history overlay.
  const refreshHistorySession = useCallback(() => {
    setPosHistoryPinSession(prev => prev ? { ...prev, at: Date.now() } : null);
  }, []);

  // Android back button / browser back navigation handler
  const backExitRef = useRef(false);
  const backExitTimerRef = useRef(null);

  useEffect(() => {
    // Push initial history entry so first back doesn't exit
    window.history.pushState({ terminal: true }, '');

    const handlePopState = () => {
      // Priority 1: Close any open overlay/modal
      if (historyOverlayOpen) {
        setHistoryOverlayOpen(false);
        window.history.pushState({ terminal: true }, '');
        return;
      }
      if (qrScannerOpen) {
        setQrScannerOpen(false);
        window.history.pushState({ terminal: true }, '');
        return;
      }
      if (docUploadOpen) {
        setDocUploadOpen(false);
        window.history.pushState({ terminal: true }, '');
        return;
      }
      if (settingsOpen) {
        setSettingsOpen(false);
        window.history.pushState({ terminal: true }, '');
        return;
      }
      if (quickScanDoc) {
        setQuickScanDoc(null);
        window.history.pushState({ terminal: true }, '');
        return;
      }
      if (showDocSearch) {
        setShowDocSearch(false);
        setDocCodeInput('');
        setDocSearchResults([]);
        window.history.pushState({ terminal: true }, '');
        return;
      }
      if (modeMenuOpen) {
        setModeMenuOpen(false);
        window.history.pushState({ terminal: true }, '');
        return;
      }

      // Priority 2: Go back to Sales tab if on another tab
      if (activeTab !== 'sales') {
        setActiveTab('sales');
        window.history.pushState({ terminal: true }, '');
        return;
      }

      // Priority 3: Double-tap to exit
      if (backExitRef.current) {
        // Second press within window — allow exit
        return;
      }
      // First press — show toast, block exit
      backExitRef.current = true;
      window.history.pushState({ terminal: true }, '');
      toast('Press back again to exit', { duration: 2000 });
      clearTimeout(backExitTimerRef.current);
      backExitTimerRef.current = setTimeout(() => { backExitRef.current = false; }, 2000);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      clearTimeout(backExitTimerRef.current);
    };
  }, [activeTab, qrScannerOpen, docUploadOpen, settingsOpen, quickScanDoc, showDocSearch, modeMenuOpen, historyOverlayOpen]);

  // Authenticated axios instance — uses tokenRef for latest token
  const [api] = useState(() => {
    const instance = axios.create({ baseURL: `${BACKEND_URL}/api` });
    instance.interceptors.request.use(config => {
      config.headers.Authorization = `Bearer ${tokenRef.current}`;
      if (config.method === 'get' && session.branchId) {
        config.params = { ...config.params, branch_id: session.branchId };
      }
      return config;
    });
    return instance;
  });

  // Token auto-refresh — refreshes every 12 hours to keep terminal connected indefinitely
  useEffect(() => {
    const REFRESH_INTERVAL = 12 * 60 * 60 * 1000; // 12 hours

    const refreshToken = async () => {
      if (!navigator.onLine) return;
      try {
        const res = await axios.post(`${BACKEND_URL}/api/terminal/refresh-token`, {}, {
          headers: { Authorization: `Bearer ${tokenRef.current}` }
        });
        const newToken = res.data.token;
        if (newToken) {
          tokenRef.current = newToken;
          if (onSessionUpdate) onSessionUpdate({ token: newToken });
        }
      } catch (err) {
        // If 401, token is fully expired — need to re-pair
        if (err?.response?.status === 401) {
          toast.error('Session expired. Please re-pair the terminal.');
          onLogout();
        }
      }
    };

    const interval = setInterval(refreshToken, REFRESH_INTERVAL);
    // Also refresh on first load if terminal has been idle
    refreshToken();

    return () => clearInterval(interval);
  }, [onLogout, onSessionUpdate]);

  // ── Smart scan helpers ────────────────────────────────────────────────────
  // Extract 8-char doc code from various input formats (URL, deeplink, raw code)
  const extractDocCode = (input) => {
    const t = input.trim();
    // Full URL containing /doc/CODE
    const urlMatch = t.match(/\/doc\/([A-Z0-9]{6,10})(?:[?/#]|$)/i);
    if (urlMatch) return urlMatch[1].toUpperCase();
    // agrismart:// deep link
    if (t.toLowerCase().startsWith('agrismart://doc/')) {
      const code = t.split('agrismart://doc/')[1]?.split(/[?/#]/)[0].toUpperCase();
      if (/^[A-Z0-9]{6,10}$/.test(code)) return code;
    }
    // Raw doc code: 8 uppercase alphanumeric, not all-digits (barcode is all-digits)
    const upper = t.toUpperCase();
    if (/^[A-Z0-9]{8}$/.test(upper) && !/^\d+$/.test(upper)) return upper;
    return null;
  };

  // Detect invoice/PO/transfer/request number patterns:
  //   KS-001 · PO-2025-001 · OS-MN-001042 · BTO-MB|SB-000060 · SR-SB|MB-000001
  // i.e. PREFIX, then optionally a branch code or a FROM|TO pair, then digits.
  const looksLikeDocNumber = (input) =>
    /^[A-Z]{1,5}[-/](?:[A-Z0-9]{1,4}(?:\|[A-Z0-9]{1,4})?[-/])?\d/i.test(input.trim());

  // Search backend by document number
  const performDocSearch = useCallback(async (query) => {
    if (!query || query.length < 2) { setDocSearchResults([]); return; }
    setDocSearchLoading(true);
    try {
      const res = await api.get('/doc/search', { params: { q: query, branch_id: session.branchId } });
      setDocSearchResults(res.data.results || []);
    } catch { setDocSearchResults([]); }
    setDocSearchLoading(false);
  }, [api, session.branchId]);

  // Route any scanned/typed input to the right action
  const handleSmartInput = useCallback(async (scanned) => {
    const docCode = extractDocCode(scanned);
    if (docCode) {
      // Always close the QR scanner overlay so the QuickScan sheet is visible.
      // This matters when the hardware scanner fires (camera is off — qrScannerRef is null)
      // as the overlay would otherwise cover the sheet at z-50.
      if (qrScannerRef.current) {
        try { await qrScannerRef.current.stop(); } catch {}
        qrScannerRef.current = null;
        setQrCameraActive(false);
      }
      setQrScannerOpen(false);   // ← always close, regardless of camera state

      // Show QuickScan sheet — fetch basic doc info and offer Reprint or View options
      setQuickScanDoc({ code: docCode, basic: null, loading: true });
      try {
        const res = await axios.get(`${BACKEND_URL}/api/doc/view/${docCode}`, {
          headers: { Authorization: `Bearer ${session.token}` },
        });
        setQuickScanDoc({ code: docCode, basic: res.data, loading: false });
      } catch {
        // Stay in-overlay — show "not found" state with Scan Again option.
        // Never navigate away; the user is mid-scan and shouldn't lose context.
        setQuickScanDoc({ code: docCode, basic: null, loading: false, notFound: true });
      }
      return;
    }
    if (looksLikeDocNumber(scanned)) {
      setDocCodeInput(scanned);
      setShowDocSearch(true);
      performDocSearch(scanned);
      return;
    }
    // Falls through — product barcode handled by TerminalSales keyboard listener
  }, [navigate, session.branchId, session.token, session.deviceId, performDocSearch]); // eslint-disable-line

  // ── QR Scanner state: 'idle' = overlay open but camera off, 'camera' = camera running
  const [qrCameraActive, setQrCameraActive] = useState(false);

  // Open the Scan QR overlay (hardware scanner ready, camera OFF — saves battery)
  const startQrScanner = useCallback(() => {
    setQrScannerOpen(true);
    setQrCameraActive(false);
    setModeMenuOpen(false);
  }, []);

  // Start the camera inside the already-open overlay (on-demand)
  const startCamera = useCallback(async () => {
    setQrCameraActive(true);
    await new Promise(r => setTimeout(r, 350));
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode('terminal-qr-scanner-view');
      qrScannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10 },
        (decodedText) => {
          const now = Date.now();
          if (decodedText === qrLastScanRef.current.code && now - qrLastScanRef.current.time < QR_SCAN_COOLDOWN) return;
          qrLastScanRef.current = { code: decodedText, time: now };
          if (navigator.vibrate) navigator.vibrate(150);
          handleSmartInput(decodedText);
        },
        () => {}
      );
    } catch (e) {
      console.error('QR Scanner error:', e);
      toast.error('Camera access denied or unavailable');
      setQrCameraActive(false);
    }
  }, [handleSmartInput, QR_SCAN_COOLDOWN]);

  const stopQrScanner = useCallback(async () => {
    if (qrScannerRef.current) {
      try { await qrScannerRef.current.stop(); } catch {}
      qrScannerRef.current = null;
    }
    setQrScannerOpen(false);
    setQrCameraActive(false);
  }, []);

  // Cleanup QR scanner on unmount
  useEffect(() => {
    return () => {
      if (qrScannerRef.current) {
        try { qrScannerRef.current.stop(); } catch {}
        qrScannerRef.current = null;
      }
    };
  }, []);

  // Global keyboard scanner — intercepts H10P HID hardware scanner output in any tab
  useEffect(() => {
    const handleGlobalKey = (e) => {
      const isInInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable;
      // When the QR scanner overlay is open, accept hardware scanner from anywhere (including inputs).
      // Otherwise, skip when an input is focused to avoid polluting search/form fields.
      if (isInInput && !qrScannerOpenRef.current) return;

      if (e.key === 'Enter') {
        // Scanner sent Enter — flush and process immediately
        const scanned = globalScanBufferRef.current.trim();
        globalScanBufferRef.current = '';
        clearTimeout(globalScanTimerRef.current);
        if (scanned.length >= 3) {
          if (qrScannerOpenRef.current && isInInput) e.preventDefault();
          handleSmartInput(scanned);
        }
        return;
      }

      if (e.key.length === 1) {
        globalScanBufferRef.current += e.key;
        clearTimeout(globalScanTimerRef.current);

        // ── Early-exit: process as soon as the buffer contains a complete doc URL ──
        // Don't wait for Enter or silence — the scanner has given us everything we need.
        // Require exactly 8 chars for the doc code (AgriBooks codes are always 8 chars).
        // Using {6,10} caused premature firing at 6 chars → partial code → not found.
        const buf = globalScanBufferRef.current;
        const completeDocUrl = /\/doc\/([A-Z0-9]{8})(?:[?/#\s]|$)/i.test(buf);
        if (completeDocUrl && qrScannerOpenRef.current) {
          clearTimeout(globalScanTimerRef.current);
          globalScanBufferRef.current = '';
          handleSmartInput(buf.trim());
          return;
        }

        // ── Silence-based flush (150ms) ─────────────────────────────────────────
        // Many HID scanners don't send Enter. After 150ms of no new characters
        // (scanner is done), process the buffer if we're in QR scanner mode
        // AND the content contains a valid doc URL pattern.
        // Outside QR scanner mode: just clear (prevents accidental product barcode routing).
        globalScanTimerRef.current = setTimeout(() => {
          const scanned = globalScanBufferRef.current.trim();
          globalScanBufferRef.current = '';
          if (qrScannerOpenRef.current && scanned.length >= 8) {
            // Only process if the buffer actually contains a recognisable doc reference.
            // Prevents partial/noise content (e.g. first few chars of a product barcode)
            // from reaching handleSmartInput and showing a "document not found" error.
            const hasDocPattern = /\/doc\/[A-Z0-9]{8}/i.test(scanned)
              || /agrismart:\/\/doc\//i.test(scanned)
              || (/^[A-Z0-9]{8}$/.test(scanned) && !/^\d+$/.test(scanned));
            if (hasDocPattern) handleSmartInput(scanned);
          }
        }, 150);
      }
    };
    window.addEventListener('keydown', handleGlobalKey);
    return () => { window.removeEventListener('keydown', handleGlobalKey); clearTimeout(globalScanTimerRef.current); };
  }, [handleSmartInput]);

  // Online/offline detection — Phase 4: robust connectivity check
  // navigator.onLine alone false-positives "offline" (captive portals,
  // stale browser state). We additionally ping /api/health every 30s.
  // Only flip to offline if BOTH navigator says offline AND ping fails,
  // OR ping fails 3 times in a row while navigator claims online.
  useEffect(() => {
    let pingFailures = 0;
    let timer = null;
    const probe = async () => {
      // Browser thinks we're offline — trust it (saves bandwidth)
      if (!navigator.onLine) {
        setIsOnline(false);
        pingFailures = 0;
        return;
      }
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 4000);
        const res = await fetch(`${BACKEND_URL}/api/health`, {
          method: 'GET', signal: ctrl.signal, cache: 'no-store',
        });
        clearTimeout(t);
        if (res.ok) {
          if (pingFailures > 0) {
            // Recovered
            setIsOnline(true);
          } else {
            // Make sure state is online (handles initial mount)
            setIsOnline((prev) => prev ? prev : true);
          }
          pingFailures = 0;
        } else {
          throw new Error('non-ok');
        }
      } catch {
        pingFailures += 1;
        // Browser claims online but backend unreachable: be patient — only
        // flip after 3 consecutive failures (90s window) to avoid the
        // "false offline" toast the user hit before.
        if (pingFailures >= 3) {
          setIsOnline(false);
        }
      }
    };
    const goOnline = () => { pingFailures = 0; setIsOnline(true); toast.success('Back online'); };
    const goOffline = () => { setIsOnline(false); toast('Working offline', { duration: 3000 }); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    // Run a probe shortly after mount + every 30s
    const initial = setTimeout(probe, 2000);
    timer = setInterval(probe, 30000);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      clearTimeout(initial);
      if (timer) clearInterval(timer);
    };
  }, []);

  // WebSocket connection for real-time events
  useEffect(() => {
    if (!session.terminalId) return;

    const connectWS = () => {
      try {
        const ws = new WebSocket(`${WS_URL}/api/terminal/ws/terminal/${session.terminalId}`);
        wsRef.current = ws;

        ws.onmessage = (event) => {
          const msg = JSON.parse(event.data);
          switch (msg.type) {
            case 'po_assigned':
              toast.success(`New PO: ${msg.data.po_number || 'PO'} from ${msg.data.vendor || 'vendor'}`, { duration: 5000 });
              setNotifications(prev => [...prev, { type: 'po', ...msg.data, time: Date.now() }]);
              // Auto-refresh PO list
              if (poRefreshRef.current) poRefreshRef.current();
              break;
            case 'transfer_assigned':
              toast.success(`New Transfer: ${msg.data.transfer_number || 'Transfer'}`, { duration: 5000 });
              setNotifications(prev => [...prev, { type: 'transfer', ...msg.data, time: Date.now() }]);
              if (transferRefreshRef.current) transferRefreshRef.current();
              break;
            case 'shift_closed_auto_logout':
              // Phase D: shift was closed (dual-PIN) → terminal logs out
              // automatically so the next operator can sign in fresh.
              toast.info(msg.data?.message || 'Your shift was closed — logging out.', { duration: 3500 });
              setTimeout(() => { onLogout?.(); }, 1500);
              break;
            default:
              break;
          }
        };

        ws.onclose = () => {
          wsRef.current = null;
          // Reconnect after 3 seconds
          setTimeout(() => { if (navigator.onLine) connectWS(); }, 3000);
        };

        ws.onerror = () => { ws.close(); };
      } catch { /* WebSocket not available */ }
    };

    if (navigator.onLine) connectWS();

    return () => { if (wsRef.current) { wsRef.current.close(); wsRef.current = null; } };
  }, [session.terminalId]);

  // ── Phase D: Session heartbeat — detect shift-close auto-logout ──────────
  // Polls /api/terminal/session every 30s. The backend returns 410 with
  // detail.code='shift_closed_auto_logout' when the user's shift was closed
  // (dual-PIN). This is the fallback path when the WebSocket is offline.
  useEffect(() => {
    let cancelled = false;
    const heartbeat = async () => {
      if (cancelled || !navigator.onLine) return;
      try {
        await api.get('/terminal/session');
      } catch (err) {
        const detail = err?.response?.data?.detail;
        if (detail && typeof detail === 'object' && detail.code === 'shift_closed_auto_logout') {
          toast.info(detail.message || 'Your shift was closed — logging out.', { duration: 3500 });
          setTimeout(() => { if (!cancelled) onLogout?.(); }, 1500);
        }
      }
    };
    const interval = setInterval(heartbeat, 30000); // 30 s
    return () => { cancelled = true; clearInterval(interval); };
  }, [onLogout]);

  // ── Background delta sync (non-blocking) ─────────────────────────────────
  const backgroundSync = useCallback(async (isFullSync = false) => {
    if (!navigator.onLine) return;
    setBackgroundSyncStatus('syncing');
    try {
      const params = { branch_id: session.branchId };
      // Delta: pass last_sync timestamp unless forcing full sync
      if (!isFullSync) {
        const lastSync = await getLastSyncTime();
        if (lastSync) params.last_sync = lastSync;
      }

      const [posRes, custRes, schemeRes] = await Promise.all([
        api.get('/sync/pos-data', { params }),
        api.get('/customers', { params: { limit: 500, branch_id: session.branchId } }),
        api.get('/price-schemes'),
      ]);

      const isDelta = posRes.data.is_delta;
      const products = posRes.data.products || [];
      const customers = custRes.data.customers || posRes.data.customers || [];
      const inventory = posRes.data.inventory || [];
      const branchPrices = posRes.data.branch_prices || [];
      const deletedIds = posRes.data.deleted_ids || [];

      if (isDelta && products.length > 0) {
        // Delta merge — only upsert changed records
        await mergeProducts(products, deletedIds);
      } else if (products.length > 0) {
        // Full replace
        await cacheProducts(products);
      }

      // Always full-replace these (lightweight collections)
      if (customers.length > 0) await cacheCustomers(customers);
      await cachePriceSchemes(schemeRes.data || posRes.data.price_schemes || []);

      if (inventory.length) {
        await cacheInventory(
          inventory.map(item => ({
            product_id: item.product_id, quantity: item.quantity ?? 0,
            branch_id: item.branch_id, updated_at: item.updated_at || new Date().toISOString(),
          }))
        );
      }
      if (branchPrices.length) {
        await cacheBranchPrices(
          branchPrices.map(bp => ({
            product_id: bp.product_id, prices: bp.prices || {},
            cost_price: bp.cost_price ?? null, branch_id: bp.branch_id,
          }))
        );
      }

      // Save sync timestamp
      await setLastSyncTime(posRes.data.sync_time || new Date().toISOString());
      // C-1 (Audit 2026-02): admin_pin_hash deprecated; cache identity-only grants
      await setOfflineAdminPinHash(null);
      if (Array.isArray(posRes.data.offline_pin_grants)) {
        const identityOnly = posRes.data.offline_pin_grants.map((g) => ({
          verifier_id: g.verifier_id,
          verifier_name: g.verifier_name,
          method: g.method,
          role: g.role,
        }));
        await setOfflinePinGrants(identityOnly);
      }

      // Sync pending offline sales
      const count = await getPendingSaleCount();
      setPendingCount(count);
      if (count > 0) {
        await syncPendingSales();
        setPendingCount(await getPendingSaleCount());
      }

      // Fetch business info for receipt printing + cache for offline use
      api.get('/settings/business-info').then(async (r) => {
        const bizData = r.data || {};
        // Convert logo to base64 for offline embedding (non-blocking)
        if (bizData.logo_url) {
          try {
            const b64 = await urlToBase64(bizData.logo_url);
            if (b64) bizData.logo_base64 = b64;
          } catch { /* keep logo_url only */ }
        }
        setBusinessInfo(bizData);
        await saveBusinessInfoCache(bizData);
      }).catch(() => {
        // Offline: load from cache silently
        getCachedBusinessInfo().then(cached => { if (cached) setBusinessInfo(cached); }).catch(() => {});
      });

      // Cache org-configured timezone — single source of truth for all
      // date/time stamps shown on this terminal (sales dates, "Last Sent",
      // "Created at", receipt headers, etc.). Refreshed on every sync so
      // an owner who changes the TZ in the web Settings sees it propagate
      // to terminals within a sync cycle.
      api.get('/settings/timezone').then(r => {
        if (r?.data?.timezone) {
          try { localStorage.setItem('agribooks.org_tz', r.data.timezone); } catch (e) { /* private mode */ }
        }
      }).catch(() => { /* offline / endpoint unavailable — keep cached value */ });

      setBackgroundSyncStatus('done');
      // Trigger TerminalSales to re-read cache
      setSyncVersion(v => v + 1);
      // Show subtle toast only when delta brought actual changes
      if (isDelta && products.length > 0) {
        toast.success(`${products.length} product(s) updated`, { duration: 2000 });
      }
      // Clear "done" indicator after 3s
      setTimeout(() => setBackgroundSyncStatus(''), 3000);
    } catch (e) {
      console.error('Background sync failed:', e);
      setBackgroundSyncStatus('error');
      setTimeout(() => setBackgroundSyncStatus(''), 5000);
    }
  }, [api, session.branchId]);

  // ── Initial data load — Instant from cache, background sync ─────────────
  const loadData = useCallback(async () => {
    if (session.organizationId) setOfflineOrg(session.organizationId);

    // Load cached business info immediately (before sync) so receipts work offline from first render
    getCachedBusinessInfo().then(cached => { if (cached) setBusinessInfo(cached); }).catch(() => {});

    // Step 1: Check if we have cached data in IndexedDB
    const cachedCount = await getProductCount();

    if (cachedCount > 0) {
      // ✅ INSTANT LOAD — show terminal immediately from cache
      setDataReady(true);
      setSyncing(false);
      // Kick off background delta sync (non-blocking)
      backgroundSync(false);
    } else {
      // First time — must do full download (no cache yet)
      setSyncing(true);
      setSyncProgress('Connecting...');

      if (navigator.onLine) {
        try {
          setSyncProgress('Downloading products...');
          const params = { branch_id: session.branchId };
          const [posRes, custRes, schemeRes] = await Promise.all([
            api.get('/sync/pos-data', { params }),
            api.get('/customers', { params: { limit: 500, ...params } }),
            api.get('/price-schemes'),
          ]);

          setSyncProgress('Saving to local storage...');
          await Promise.all([
            cacheProducts(posRes.data.products || []),
            cacheCustomers(custRes.data.customers || posRes.data.customers || []),
            cachePriceSchemes(schemeRes.data || posRes.data.price_schemes || []),
            posRes.data.inventory?.length ? cacheInventory(
              posRes.data.inventory.map(item => ({
                product_id: item.product_id, quantity: item.quantity ?? 0,
                branch_id: item.branch_id, updated_at: item.updated_at || new Date().toISOString(),
              }))
            ) : Promise.resolve(),
            posRes.data.branch_prices?.length ? cacheBranchPrices(
              posRes.data.branch_prices.map(bp => ({
                product_id: bp.product_id, prices: bp.prices || {},
                cost_price: bp.cost_price ?? null, branch_id: bp.branch_id,
              }))
            ) : Promise.resolve(),
          ]);

          await setLastSyncTime(posRes.data.sync_time || new Date().toISOString());
          // C-1 (Audit 2026-02): admin_pin_hash deprecated; identity-only grants
          await setOfflineAdminPinHash(null);
          if (Array.isArray(posRes.data.offline_pin_grants)) {
            const identityOnly = posRes.data.offline_pin_grants.map((g) => ({
              verifier_id: g.verifier_id,
              verifier_name: g.verifier_name,
              method: g.method,
              role: g.role,
            }));
            await setOfflinePinGrants(identityOnly);
          }

          const count = await getPendingSaleCount();
          setPendingCount(count);
          if (count > 0) {
            setSyncProgress(`Syncing ${count} pending sale(s)...`);
            await syncPendingSales();
            setPendingCount(await getPendingSaleCount());
          }

          setSyncProgress('');
          setDataReady(true);
          setSyncVersion(v => v + 1); // Update last-synced display
          toast.success(`Data synced — ${posRes.data.products?.length || 0} products loaded`);
          // Fetch and cache business info (with logo base64 for offline)
          api.get('/settings/business-info').then(async (r) => {
            const bizData = r.data || {};
            if (bizData.logo_url) {
              try { const b64 = await urlToBase64(bizData.logo_url); if (b64) bizData.logo_base64 = b64; } catch {}
            }
            setBusinessInfo(bizData);
            await saveBusinessInfoCache(bizData);
          }).catch(() => {
            getCachedBusinessInfo().then(cached => { if (cached) setBusinessInfo(cached); }).catch(() => {});
          });
          // Cache org timezone alongside business info (single source of truth).
          api.get('/settings/timezone').then(r => {
            if (r?.data?.timezone) {
              try { localStorage.setItem('agribooks.org_tz', r.data.timezone); } catch (e) { /* private mode */ }
            }
          }).catch(() => {});
        } catch (e) {
          console.error('Sync failed:', e);
          await loadOfflineData();
        }
      } else {
        await loadOfflineData();
      }
      setSyncing(false);
    }
  }, [api, session.branchId, session.organizationId, backgroundSync]);

  const loadOfflineData = async () => {
    const prods = await getProducts();
    if (prods.length > 0) {
      setDataReady(true);
      toast('Loaded from offline cache', { duration: 3000 });
    } else {
      toast.error('No cached data — connect to internet first');
    }
    setSyncProgress('');
  };

  useEffect(() => { loadData(); }, [loadData]);

  // Bind this device's ID to the terminal session (handles code-based pairing
  // where device_id wasn't available at pairing time; idempotent, fire-and-forget)
  useEffect(() => {
    if (!session.terminalId) return;
    DeviceIdentity.getDeviceId()
      .then(({ deviceId }) => {
        if (!deviceId) return;
        // Update local session with deviceId if not already set
        if (!session.deviceId && onSessionUpdate) onSessionUpdate({ deviceId });
        // Bind to backend session
        axios.post(`${BACKEND_URL}/api/terminal/bind-device`, {
          terminal_id: session.terminalId,
          device_id: deviceId,
        }, { headers: { Authorization: `Bearer ${tokenRef.current}` } }).catch(() => {}); // fire-and-forget
      })
      .catch(() => {});
  }, [session.terminalId]); // eslint-disable-line

  // Auto sync
  useEffect(() => {
    startAutoSync(() => session.branchId);
    return () => stopAutoSync();
  }, [session.branchId]);

  // "Last Synced: X min ago" ticker — updates display every 30s
  useEffect(() => {
    let mounted = true;
    const updateDisplay = async () => {
      const ts = await getLastSyncTime();
      if (mounted) setLastSyncDisplay(formatRelativeTime(ts));
    };
    updateDisplay();
    const timer = setInterval(updateDisplay, 30000);
    return () => { mounted = false; clearInterval(timer); };
  }, [syncVersion]); // re-run when sync completes

  // Expose __triggerBackgroundSync for Android Capacitor onResume hook
  useEffect(() => {
    window.__triggerBackgroundSync = () => backgroundSync(false);
    return () => { delete window.__triggerBackgroundSync; };
  }, [backgroundSync]);

  // Auto-sync prices every 5 minutes so the terminal doesn't show stale
  // retail / wholesale / capital data after a price update on the web app.
  // Delta sync: only fetches records changed since the last sync timestamp,
  // so it's lightweight (typically <50 products on an active branch).
  useEffect(() => {
    const AUTO_SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes
    const timer = setInterval(() => {
      if (navigator.onLine) backgroundSync(false);
    }, AUTO_SYNC_INTERVAL);
    return () => clearInterval(timer);
  }, [backgroundSync]);

  const handleManualSync = async () => {
    setSyncing(true);
    await backgroundSync(true); // Force full sync on manual trigger
    setSyncing(false);
  };

  const handleLogout = () => {
    if (wsRef.current) wsRef.current.close();
    localStorage.removeItem('agrismart_terminal');
    onLogout();
  };

  // ── Remote Printing — print-device claim contract ─────────────────────────
  // The terminal acts as a "print receiver": paired once via a code entered
  // in Print Center, then it claims jobs every ~3s with its X-Receiver-Token
  // and prints them through the SAME local pipeline as on-terminal receipts
  // (PrintBridge → H10P text-mode/bitmap on device, popup on desktop browser).

  // 401 = token revoked/unknown → wipe pairing. 403 = disabled/expired → stop
  // claiming and surface the reason. Returns true when the error was handled.
  const handleDeviceAuthError = useCallback((err) => {
    const status = err?.response?.status;
    if (status === 401) {
      wipeDevice();
      setPrintDevice(null);
      setPrintQueue([]);
      toast.error('Remote printing unpaired — pair this terminal again in Print Center');
      return true;
    }
    if (status === 403) {
      const detail = err?.response?.data?.detail;
      const msg = (typeof detail === 'object' ? detail?.message : detail) || 'Print device disabled';
      setClaimPaused(msg);
      return true;
    }
    return false;
  }, []);

  // Uploaded ("external") documents have no PrintEngine mapping — print the
  // raw file via the shell's auto-print popup idiom (same as the old WS path).
  const printExternalFile = (job) => {
    const url = job.file_url || '';
    if (!url) throw new Error('Uploaded document has no file URL');
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) { toast.error('Please allow popups to print'); throw new Error('Popup blocked'); }
    const isImage = /^image\//i.test(job.file_format || '') || /\.(png|jpe?g|webp|gif|bmp)(\?|$)/i.test(url);
    if (isImage) {
      win.document.write(`<!DOCTYPE html><html><head><title>Print</title><style>body{margin:0}img{max-width:100%}</style></head><body><img src="${url}" onload="window.print();setTimeout(function(){window.close();},2000)" onerror="window.close()"/></body></html>`);
      win.document.close();
    } else {
      // PDF / unknown — let the browser's viewer render it, then invoke print
      win.location = url;
      setTimeout(() => { try { win.focus(); win.print(); } catch { /* viewer handles it */ } }, 1500);
    }
    win.focus();
  };

  // Print one claimed job: pull render data, render with PrintEngine locally,
  // print via PrintBridge, then ack /done (or /failed with the real reason).
  const printClaimedJob = useCallback(async (job) => {
    const device = printDeviceRef.current;
    if (!device?.token || !job?.id) return;
    if (ledgerHas(job.id)) {
      // Printed before a crash lost the ack — ack now, never re-print.
      jobDone(device.token, job.id).catch(() => {});
      setPrintQueue(prev => prev.filter(j => j.id !== job.id));
      return;
    }
    printingRef.current = true;
    // Lease keep-alive while a long doc prints (fire-and-forget)
    const lease = setInterval(() => { jobProgress(device.token, job.id).catch(() => {}); }, 20000);
    try {
      if (job.document_type === 'uploaded_document' || job.source_type === 'external') {
        printExternalFile(job);
      } else {
        const rd = await fetchRenderData(device.token, job);
        let doc = rd.document || {};
        let type = rd.document_type === 'invoice' ? PrintEngine.getDocType(doc)
          : rd.document_type === 'expense' ? 'expense_voucher'
          : rd.document_type; // purchase_order / branch_transfer / proof_of_payment as-is
        if (rd.document_type === 'capital_change_report') {
          // Report snapshot (2026-09-03): the frozen report sits in `data`;
          // `kind` picks the layout (strip = thermal list, sheet = DM table).
          type = doc.kind === 'strip' ? 'capital_price_strip' : 'capital_change_report';
          doc = doc.data || {};
        }
        const format = job.print_format || 'thermal';
        const copies = Math.max(1, Math.min(parseInt(job.copies) || 1, 5));
        for (let copy = 1; copy <= copies; copy++) {
          await PrintBridge.print({
            type, data: doc, format,
            businessInfo: rd.business_info || businessInfo || {},
            docCode: rd.doc_code || job.doc_code || '',
            copyNumber: copy,
          });
          if (copy < copies) {
            const gap = await PrintBridge.interCopyGapMs({ data: doc, format });
            await new Promise(r => setTimeout(r, gap));
          }
        }
      }
      ledgerAdd(job.id);
      await jobDone(device.token, job.id).catch(() => { /* done retried via ledger on re-claim */ });
      setPrintQueue(prev => prev.filter(j => j.id !== job.id));
      toast.success(`Printed: ${job.document_name || 'Document'}`, { duration: 2500 });
    } catch (err) {
      if (!handleDeviceAuthError(err)) {
        const msg = err?.response?.data?.detail || err?.message || 'Print failed on the terminal';
        jobFailed(device.token, job.id, String(msg)).catch(() => {});
        setPrintQueue(prev => prev.filter(j => j.id !== job.id));
        toast.error(`Print failed: ${String(msg)}`);
      }
    } finally {
      clearInterval(lease);
      printingRef.current = false;
    }
  }, [businessInfo, handleDeviceAuthError]);

  // Route a freshly claimed job: auto/urgent → print now; manual → queue it.
  const processClaimedJob = useCallback(async (job) => {
    const device = printDeviceRef.current;
    if (!job?.id) return;
    if (ledgerHas(job.id)) {
      // Double-print ledger hit — ack without printing.
      if (device?.token) jobDone(device.token, job.id).catch(() => {});
      return;
    }
    if (printModeRef.current === 'auto' || job.priority === 'urgent') {
      toast.success(`Printing: ${job.document_name || 'Document'}`, { duration: 3000 });
      await printClaimedJob(job);
    } else {
      setPrintQueue(prev => (prev.find(j => j.id === job.id) ? prev : [job, ...prev]));
      setPrintQueueOpen(true);
      toast(`New print job: ${job.document_name || 'Document'}`, {
        duration: 5000,
        action: { label: 'Print Now', onClick: () => { printClaimedJob(job); } },
      });
    }
  }, [printClaimedJob]);

  // Claim loop — every ~3s, gated on connectivity, paused while printing.
  useEffect(() => {
    if (!printDevice?.token || claimPaused) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled || !navigator.onLine || printingRef.current) return;
      try {
        const jobs = await claimJobs(printDevice.token);
        for (const job of jobs) {
          if (cancelled) break;
          await processClaimedJob(job);
        }
      } catch (err) { handleDeviceAuthError(err); }
    };
    tick();
    const timer = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [printDevice?.token, claimPaused, processClaimedJob, handleDeviceAuthError]);

  // Heartbeat — every ~30s; reports capabilities + drives Print Center's online dot.
  useEffect(() => {
    if (!printDevice?.token) return;
    const beat = () => {
      if (!navigator.onLine) return;
      deviceHeartbeat(printDevice.token, { thermal: true, dot_matrix: false }, 'terminal-web')
        .catch(handleDeviceAuthError);
    };
    beat();
    const timer = setInterval(beat, 30000);
    return () => clearInterval(timer);
  }, [printDevice?.token, handleDeviceAuthError]);

  // Lease keep-alive for jobs parked in the MANUAL queue — without this the
  // server would release them for retry after 90s of no progress pings.
  useEffect(() => {
    if (!printDevice?.token || printQueue.length === 0) return;
    const timer = setInterval(() => {
      if (!navigator.onLine) return;
      printQueue.forEach(job => { jobProgress(printDevice.token, job.id).catch(() => {}); });
    }, 20000);
    return () => clearInterval(timer);
  }, [printDevice?.token, printQueue]);

  // Pairing: get a code to display, then poll it (token arrives EXACTLY once).
  const startPairing = useCallback(async () => {
    try {
      const { code } = await generatePairingCode();
      setPairing({ code, status: 'pending' });
    } catch {
      toast.error('Could not get a pairing code — check the connection');
    }
  }, []);

  useEffect(() => {
    if (!pairing?.code || pairing.status !== 'pending') return;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const res = await pollPairing(pairing.code);
        if (cancelled) return;
        if (res.status === 'paired' && res.token) {
          const device = {
            token: res.token,
            name: res.receiver?.name || 'This terminal',
            branch_name: res.receiver?.branch_name || '',
          };
          storeDevice(device);
          setPrintDevice(device);
          setClaimPaused('');
          setPairing(null);
          toast.success(`Remote printing enabled — paired as "${device.name}"`);
        } else if (res.status === 'expired' || res.status === 'invalid') {
          setPairing(p => (p ? { ...p, status: 'expired' } : null));
        }
      } catch { /* keep polling */ }
    }, 2500);
    return () => { cancelled = true; clearInterval(timer); };
  }, [pairing?.code, pairing?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Local disable — wipes the token; the device row stays in Print Center
  // until an admin disables/removes it there.
  const disablePrintDevice = useCallback(() => {
    wipeDevice();
    setPrintDevice(null);
    setPrintQueue([]);
    setClaimPaused('');
    toast('Remote printing disabled on this terminal', { duration: 2500 });
  }, []);

  // Notification badge count (unread)
  const unreadCount = notifications.filter(n => Date.now() - n.time < 60000).length;

  if (!dataReady) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center text-white" data-testid="terminal-loading">
        <RefreshCw className="w-10 h-10 animate-spin text-emerald-400 mb-4" />
        <p className="text-slate-300 text-sm">{syncProgress || 'Preparing terminal...'}</p>
        <p className="text-slate-500 text-xs mt-2">{session.branchName}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex flex-col" data-testid="terminal-shell">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-3 py-2 flex items-center justify-between safe-area-top">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-[#1A4D2E]" style={{ fontFamily: 'Manrope' }}>AgriSmart</span>
          <span className="text-xs text-slate-500 border-l border-slate-200 pl-2">{session.branchName}</span>
          {/* Phase D — Multi-shift active-shift badge */}
          {shiftStatus.multiShiftEnabled && shiftStatus.activeShift && (
            <span className="flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
              data-testid="terminal-shift-badge"
              title={`Shift opened — opening float ₱${shiftStatus.activeShift.opening_float || 0}`}>
              <Check size={9} /> {shiftStatus.activeShift.shift_id}
            </span>
          )}
          {/* Background sync indicator */}
          {backgroundSyncStatus === 'syncing' && (
            <span className="flex items-center gap-1 text-[10px] text-amber-600" data-testid="sync-indicator-syncing">
              <RefreshCw size={10} className="animate-spin" /> Syncing
            </span>
          )}
          {backgroundSyncStatus === 'done' && (
            <span className="flex items-center gap-1 text-[10px] text-emerald-600" data-testid="sync-indicator-done">
              <Check size={10} /> Up to date
            </span>
          )}
          {backgroundSyncStatus === 'error' && (
            <span className="flex items-center gap-1 text-[10px] text-red-500" data-testid="sync-indicator-error">
              <WifiOff size={10} /> Sync failed
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Smart Doc Search — accepts 8-char doc codes, invoice numbers, PO numbers */}
          {showDocSearch ? (
            <div className="relative">
              <div className="flex items-center gap-1">
                <input
                  autoFocus
                  type="text"
                  value={docCodeInput}
                  onChange={e => {
                    const v = e.target.value.toUpperCase();
                    setDocCodeInput(v);
                    // Auto-navigate if it looks like a raw 8-char doc code
                    const code = v.trim();
                    if (/^[A-Z0-9]{8}$/.test(code) && !/^\d+$/.test(code)) {
                      navigate(`/doc/${code}?branch=${session.branchId}&device=${session.deviceId || ''}`);
                      setShowDocSearch(false); setDocCodeInput(''); setDocSearchResults([]);
                      return;
                    }
                    performDocSearch(v);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      const v = docCodeInput.trim();
                      if (v.length >= 2) { handleSmartInput(v); setShowDocSearch(false); setDocCodeInput(''); setDocSearchResults([]); }
                    }
                    if (e.key === 'Escape') { setShowDocSearch(false); setDocCodeInput(''); setDocSearchResults([]); }
                  }}
                  placeholder="Code or invoice #..."
                  maxLength={20}
                  className="h-7 w-36 text-center font-mono text-sm rounded-lg border border-slate-200 bg-white px-2 uppercase tracking-widest"
                  data-testid="terminal-doc-code-input"
                />
                {docSearchLoading && <Loader2 size={12} className="animate-spin text-slate-400" />}
                <button onClick={() => { setShowDocSearch(false); setDocCodeInput(''); setDocSearchResults([]); }} className="h-7 px-1.5 rounded-lg border border-slate-200 text-slate-400 text-xs">
                  <X size={12} />
                </button>
              </div>
              {/* Search results dropdown */}
              {docSearchResults.length > 0 && (
                <div className="absolute top-8 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden min-w-[240px]" data-testid="doc-search-results">
                  {docSearchResults.map((r, i) => (
                    <button key={i} onClick={() => { navigate(`/doc/${r.doc_code}?branch=${session.branchId}&device=${session.deviceId || ''}`); setShowDocSearch(false); setDocCodeInput(''); setDocSearchResults([]); }}
                      className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-emerald-50 border-b border-slate-50 last:border-0"
                      data-testid={`doc-search-result-${r.doc_code}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs font-bold text-slate-800 truncate">{r.number}</p>
                          {r.is_offline_receipt && (
                            <span className="text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0" data-testid={`offline-badge-${r.doc_code}`}>
                              Offline
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 truncate">{r.label}</p>
                      </div>
                      <div className="text-right ml-2 shrink-0">
                        <p className="text-[10px] text-emerald-700">{r.doc_code}</p>
                        <p className="text-[9px] text-slate-400 capitalize">{r.doc_type.replace('_', ' ')}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => setShowDocSearch(true)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" title="Find by doc code or invoice number" data-testid="terminal-find-code-btn">
              <Search size={14} />
            </button>
          )}
          {pendingCount > 0 && (
            <span className="bg-amber-100 text-amber-700 text-[10px] font-medium px-2 py-0.5 rounded-full" data-testid="pending-badge">
              {pendingCount} pending
            </span>
          )}
          <span className={`flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${isOnline ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`} data-testid="online-status">
            {isOnline ? <Wifi size={10} /> : <WifiOff size={10} />}
            {isOnline ? 'Online' : 'Offline'}
          </span>
          <button onClick={handleManualSync} disabled={syncing} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="sync-btn">
            <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Phase D — Shift required banner (multi-shift mode only) */}
      {shiftStatus.multiShiftEnabled && shiftStatus.requiresShiftOpen && !shiftStatus.activeShift && (
        <div className="bg-amber-50 border-b border-amber-200 px-3 py-2 flex items-start gap-2"
          data-testid="terminal-shift-required-banner">
          <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1 text-[11px] text-amber-900 leading-snug">
            <strong>Shift not open.</strong> Sales will be rejected for accountability.
            Open your shift in the web app (Sales tab → Open Shift) first.
          </div>
        </div>
      )}

      {/* Content */}
      <div className={`flex-1 overflow-hidden ${railExpanded ? 'lg:pl-60' : 'lg:pl-14'} transition-[padding] duration-150`}>
        {activeTab === 'sales' && (
          <TerminalSales api={api} session={session} isOnline={isOnline} pendingCount={pendingCount} setPendingCount={setPendingCount} syncVersion={syncVersion} />
        )}
        {activeTab === 'po' && (
          <TerminalPOCheck api={api} session={session} isOnline={isOnline} onRefreshRef={poRefreshRef} />
        )}
        {activeTab === 'transfers' && (
          <TerminalTransfers api={api} session={session} isOnline={isOnline} onRefreshRef={transferRefreshRef} />
        )}
      </div>

      {/* ── Tablet+ Side Rail (lg and up) — collapsible (icon-only by default) ─── */}
      <aside
        className={`hidden lg:flex flex-col fixed top-12 bottom-0 left-0 bg-white border-r border-slate-200 z-40 overflow-y-auto transition-[width] duration-150 ${railExpanded ? 'w-60' : 'w-14'}`}
        data-testid="terminal-side-rail"
        data-rail-state={railExpanded ? 'expanded' : 'collapsed'}
      >
        {/* Header / expand-collapse toggle */}
        <div className={`flex items-center border-b border-slate-100 ${railExpanded ? 'px-3 py-3 justify-between gap-2' : 'px-0 py-2 justify-center'}`}>
          {railExpanded && (
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Workspace</p>
              <p className="text-sm font-bold text-slate-800 truncate" title={session.branchName}>{session.branchName}</p>
            </div>
          )}
          <button
            onClick={() => setRailExpanded(v => !v)}
            className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-slate-100 text-slate-500 shrink-0"
            data-testid="rail-toggle"
            title={railExpanded ? 'Collapse menu' : 'Expand menu'}
            aria-label={railExpanded ? 'Collapse menu' : 'Expand menu'}
          >
            {railExpanded ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>

        <nav className="py-2">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            const hasBadge = (tab.key === 'po' && notifications.some(n => n.type === 'po' && Date.now() - n.time < 60000)) ||
                             (tab.key === 'transfers' && notifications.some(n => n.type === 'transfer' && Date.now() - n.time < 60000));
            return (
              <button key={tab.key}
                onClick={() => { setActiveTab(tab.key); setNotifications(prev => prev.filter(n => n.type !== (tab.key === 'po' ? 'po' : 'transfer'))); }}
                className={`w-full flex items-center transition-colors ${railExpanded ? 'gap-3 px-3 py-2.5 text-left' : 'justify-center py-2'} ${active ? 'bg-[#1A4D2E] text-white' : 'hover:bg-slate-50 text-slate-700'}`}
                data-testid={`rail-${tab.key}`}
                title={tab.label}
                aria-label={tab.label}
              >
                <div className={`relative w-8 h-8 rounded-lg flex items-center justify-center ${active ? 'bg-white/20' : tab.color}`}>
                  <Icon size={16} />
                  {hasBadge && !railExpanded && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" />
                  )}
                </div>
                {railExpanded && <span className="text-sm font-medium flex-1">{tab.label}</span>}
                {railExpanded && hasBadge && <span className="w-2 h-2 bg-red-500 rounded-full" />}
              </button>
            );
          })}
        </nav>

        <div className="border-t border-slate-100 py-2">
          {railExpanded && (
            <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Tools</p>
          )}
          {[
            { onClick: startQrScanner, testId: 'rail-scan-qr', label: 'Scan QR', Icon: ScanLine, iconCls: 'bg-purple-50 text-purple-600' },
            { onClick: () => setCollectPaymentOpen(true), testId: 'rail-collect-payment', label: 'Collect Payment', Icon: Wallet, iconCls: 'bg-emerald-50 text-emerald-700' },
            { onClick: () => setCustomerAccountOpen(true), testId: 'rail-customer-account', label: 'Customer Account', Icon: User, iconCls: 'bg-blue-50 text-blue-700' },
            { onClick: () => setDocUploadOpen(true), testId: 'rail-upload-doc', label: 'Upload Doc', Icon: FolderUp, iconCls: 'bg-teal-50 text-teal-600' },
            { onClick: () => setHistoryOverlayOpen(true), testId: 'rail-history', label: 'History', Icon: History, iconCls: 'bg-rose-50 text-rose-600' },
          ].map(({ onClick, testId, label, Icon, iconCls }) => (
            <button key={testId} onClick={onClick}
              className={`w-full flex items-center hover:bg-slate-50 text-slate-700 ${railExpanded ? 'gap-3 px-3 py-2.5 text-left' : 'justify-center py-2'}`}
              data-testid={testId}
              title={label}
              aria-label={label}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconCls}`}>
                <Icon size={16} />
              </div>
              {railExpanded && <span className="text-sm font-medium">{label}</span>}
            </button>
          ))}
          {/* Print Queue (badge needs special treatment) */}
          <button onClick={() => setPrintQueueOpen(true)}
            className={`w-full flex items-center hover:bg-slate-50 text-slate-700 ${railExpanded ? 'gap-3 px-3 py-2.5 text-left' : 'justify-center py-2'}`}
            data-testid="rail-print-queue"
            title="Print Queue"
            aria-label="Print Queue"
          >
            <div className="relative w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-600">
              <Printer size={16} />
              {printQueue.length > 0 && (
                <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 bg-red-500 rounded-full text-white text-[9px] flex items-center justify-center font-bold ring-2 ring-white">
                  {printQueue.length}
                </span>
              )}
            </div>
            {railExpanded && <span className="text-sm font-medium flex-1">Print Queue</span>}
            {railExpanded && printQueue.length > 0 && (
              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
                {printQueue.length}
              </span>
            )}
          </button>
        </div>

        <div className="border-t border-slate-100 py-2 mt-auto">
          <button onClick={() => setSettingsOpen(true)}
            className={`w-full flex items-center hover:bg-slate-50 text-slate-500 ${railExpanded ? 'gap-3 px-3 py-2.5 text-left' : 'justify-center py-2'}`}
            data-testid="rail-settings"
            title="Settings" aria-label="Settings">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-100">
              <Settings size={16} />
            </div>
            {railExpanded && <span className="text-sm font-medium">Settings</span>}
          </button>
          <button onClick={handleLogout}
            className={`w-full flex items-center hover:bg-red-50 text-red-600 ${railExpanded ? 'gap-3 px-3 py-2.5 text-left' : 'justify-center py-2'}`}
            data-testid="rail-logout"
            title="Logout" aria-label="Logout">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-red-50">
              <Unlink size={16} />
            </div>
            {railExpanded && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Floating Mode Selector — lower left (phone/tablet portrait only) */}
      <div className="fixed bottom-4 left-4 z-50 safe-area-bottom lg:hidden" data-testid="mode-selector">
        {/* Mode menu popup */}
        {modeMenuOpen && (
          <div className="absolute bottom-14 left-0 bg-white rounded-2xl border border-slate-200 overflow-y-auto w-52 max-h-[calc(100vh-6rem)]" style={{boxShadow:'0 4px 16px rgba(0,0,0,0.12)'}}>
            {TABS.map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.key;
              const hasBadge = (tab.key === 'po' && notifications.some(n => n.type === 'po' && Date.now() - n.time < 60000)) ||
                               (tab.key === 'transfers' && notifications.some(n => n.type === 'transfer' && Date.now() - n.time < 60000));
              return (
                <button key={tab.key}
                  onClick={() => { setActiveTab(tab.key); setModeMenuOpen(false); setNotifications(prev => prev.filter(n => n.type !== (tab.key === 'po' ? 'po' : 'transfer'))); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    active ? 'bg-[#1A4D2E] text-white' : 'hover:bg-slate-50 text-slate-700'
                  }`}
                  data-testid={`mode-${tab.key}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${active ? 'bg-white/20' : tab.color}`}>
                    <Icon size={16} />
                  </div>
                  <span className="text-sm font-medium flex-1">{tab.label}</span>
                  {hasBadge && <span className="w-2.5 h-2.5 bg-red-500 rounded-full" />}
                  {active && <ChevronRight size={14} className="opacity-60" />}
                </button>
              );
            })}
            <div className="border-t border-slate-100">
              <button onClick={startQrScanner}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 text-slate-700"
                data-testid="mode-scan-qr">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-50 text-purple-600">
                  <ScanLine size={16} />
                </div>
                <span className="text-sm font-medium">Scan QR</span>
              </button>
              <button onClick={() => { setModeMenuOpen(false); setCollectPaymentOpen(true); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 text-slate-700"
                data-testid="mode-collect-payment">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-700">
                  <Wallet size={16} />
                </div>
                <span className="text-sm font-medium">Collect Payment</span>
              </button>
              <button onClick={() => { setModeMenuOpen(false); setCustomerAccountOpen(true); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 text-slate-700"
                data-testid="mode-customer-account">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-50 text-blue-700">
                  <User size={16} />
                </div>
                <span className="text-sm font-medium">Customer Account</span>
              </button>
              <button onClick={() => { setModeMenuOpen(false); setDocUploadOpen(true); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 text-slate-700"
                data-testid="mode-upload-doc">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-teal-50 text-teal-600">
                  <FolderUp size={16} />
                </div>
                <span className="text-sm font-medium">Upload Doc</span>
              </button>
              <button onClick={() => { setModeMenuOpen(false); setHistoryOverlayOpen(true); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 text-slate-700"
                data-testid="mode-history">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-rose-50 text-rose-600">
                  <History size={16} />
                </div>
                <span className="text-sm font-medium">History</span>
              </button>
              <button onClick={() => { setModeMenuOpen(false); setPrintQueueOpen(true); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 text-slate-700"
                data-testid="mode-print-queue">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-600 relative">
                  <Printer size={16} />
                  {printQueue.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[9px] flex items-center justify-center font-bold">
                      {printQueue.length}
                    </span>
                  )}
                </div>
                <span className="text-sm font-medium flex-1">Print Queue</span>
                {printQueue.length > 0 && (
                  <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
                    {printQueue.length}
                  </span>
                )}
              </button>
            </div>
            <div className="border-t border-slate-100">
              <button onClick={() => { setModeMenuOpen(false); setSettingsOpen(true); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 text-slate-500"
                data-testid="terminal-settings-btn">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-100">
                  <Settings size={16} />
                </div>
                <span className="text-sm font-medium">Settings</span>
              </button>
              <button onClick={() => { setModeMenuOpen(false); handleLogout(); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-red-50 text-red-600"
                data-testid="terminal-logout-btn">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-red-50">
                  <Unlink size={16} />
                </div>
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        )}

        {/* Floating button */}
        <button
          onClick={() => setModeMenuOpen(v => !v)}
          className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${
            modeMenuOpen ? 'bg-slate-800 text-white rotate-90' : 'bg-[#1A4D2E] text-white hover:bg-[#14532d]'
          }`}
          data-testid="mode-toggle-btn"
        >
          {(() => {
            const CurrentIcon = TABS.find(t => t.key === activeTab)?.icon || ShoppingCart;
            return modeMenuOpen ? <ChevronRight size={20} /> : <CurrentIcon size={20} />;
          })()}
        </button>

        {/* Notification dot */}
        {notifications.length > 0 && !modeMenuOpen && (
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-[#F5F5F0]" />
        )}
      </div>

      {/* Click-away backdrop when menu open */}
      {modeMenuOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setModeMenuOpen(false)} />
      )}

      {/* Settings Panel */}
      {settingsOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSettingsOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>Terminal Settings</h3>
              <p className="text-xs text-slate-400 mt-0.5">{session.branchName}</p>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div>
                  <p className="text-xs text-slate-500 font-medium">Branch</p>
                  <p className="text-sm font-semibold text-slate-800">{session.branchName}</p>
                </div>
                <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Linked</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div>
                  <p className="text-xs text-slate-500 font-medium">Paired by</p>
                  <p className="text-sm text-slate-700">{session.userName || 'Unknown'}</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div>
                  <p className="text-xs text-slate-500 font-medium">Status</p>
                  <p className="text-sm text-slate-700 flex items-center gap-1.5">
                    {isOnline ? <Wifi size={12} className="text-emerald-600" /> : <WifiOff size={12} className="text-red-500" />}
                    {isOnline ? 'Online' : 'Offline'}
                  </p>
                </div>
                <button onClick={() => { setSettingsOpen(false); handleManualSync(); }}
                  className="text-xs text-blue-600 hover:underline">Sync now</button>
              </div>
              {/* Last Synced display */}
              <button
                onClick={() => { setSettingsOpen(false); handleManualSync(); }}
                className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors text-left"
                data-testid="last-sync-row"
              >
                <div>
                  <p className="text-xs text-slate-500 font-medium">Last Synced</p>
                  <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                    <RefreshCw size={12} className={`${backgroundSyncStatus === 'syncing' ? 'animate-spin text-amber-500' : 'text-slate-400'}`} />
                    {backgroundSyncStatus === 'syncing' ? 'Syncing now...' : lastSyncDisplay || 'Never'}
                  </p>
                </div>
                <span className="text-[10px] text-blue-600 font-medium">Tap to sync</span>
              </button>
            </div>
            <div className="p-4 border-t border-slate-100 space-y-2">
              {/* Remote Printing on this terminal — print-device claim contract */}
              <div className="p-3 bg-slate-50 rounded-xl space-y-2" data-testid="remote-printing-section">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500 font-medium">Remote Printing on this terminal</p>
                    {printDevice ? (
                      <>
                        <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 truncate">
                          <Printer size={12} className="text-emerald-600 shrink-0" />
                          {printDevice.name}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {claimPaused ? '' : `Receiving print jobs${printDevice.branch_name ? ` — ${printDevice.branch_name}` : ''}`}
                        </p>
                      </>
                    ) : (
                      <p className="text-[10px] text-slate-400 mt-0.5">Other devices can print on this terminal once paired</p>
                    )}
                  </div>
                  {printDevice && (
                    <button
                      onClick={() => {
                        const next = printMode === 'auto' ? 'manual' : 'auto';
                        setPrintMode(next);
                        setAutoPrint(next === 'auto');
                        toast.success(next === 'auto' ? 'Auto Print ON — jobs print on arrival' : 'Auto Print OFF — jobs wait in the Print Queue');
                      }}
                      data-testid="toggle-print-mode-btn"
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all shrink-0 ${printMode === 'auto' ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                    >
                      <Printer size={12} className="inline mr-1" />
                      {printMode === 'auto' ? 'Auto' : 'Manual'}
                    </button>
                  )}
                </div>

                {/* 403 — disabled/expired by the server: claiming stopped */}
                {printDevice && claimPaused && (
                  <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="remote-printing-paused">
                    <AlertTriangle size={12} className="text-amber-600 mt-0.5 shrink-0" />
                    <p className="flex-1 text-[10px] text-amber-800 leading-snug">{claimPaused}</p>
                    <button onClick={() => setClaimPaused('')} className="text-[10px] text-blue-600 font-semibold hover:underline shrink-0">Retry</button>
                  </div>
                )}

                {printDevice ? (
                  <button
                    onClick={disablePrintDevice}
                    data-testid="disable-remote-printing-btn"
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-white border border-slate-300 text-slate-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors text-xs font-semibold"
                  >
                    <Unlink size={12} />
                    Disable on this terminal
                  </button>
                ) : pairing ? (
                  pairing.status === 'expired' ? (
                    <div className="space-y-2" data-testid="pairing-expired">
                      <p className="text-[10px] text-amber-700 text-center">Code expired — get a new one.</p>
                      <button onClick={startPairing}
                        className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors">
                        <RefreshCw size={12} /> Get a new code
                      </button>
                      <button onClick={() => setPairing(null)}
                        className="w-full py-1.5 text-[10px] text-slate-400 hover:text-slate-600 transition-colors">Cancel</button>
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="pairing-code-display">
                      <p className="text-2xl font-bold text-slate-900 text-center font-mono tracking-[0.3em]">{pairing.code}</p>
                      <p className="text-[10px] text-slate-400 text-center leading-snug">
                        Enter this code in Print Center to name and assign this terminal.
                      </p>
                      <p className="text-[10px] text-emerald-600 text-center flex items-center justify-center gap-1.5">
                        <Loader2 size={10} className="animate-spin" /> Waiting for pairing...
                      </p>
                      <button onClick={() => setPairing(null)}
                        className="w-full py-1.5 text-[10px] text-slate-400 hover:text-slate-600 transition-colors">Cancel</button>
                    </div>
                  )
                ) : (
                  <button
                    onClick={startPairing}
                    data-testid="enable-remote-printing-btn"
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors"
                  >
                    <Printer size={12} />
                    Enable — get pairing code
                  </button>
                )}
              </div>

              {/* DEV-ONLY: switch between per-machine dev servers
                  (dev/dev1/dev2/dev3.agri-books.com — one tunnel per computer,
                  see memory/MACHINES.md). Only rendered when the app is
                  already on a dev host, i.e. the DEV flavor of the APK. */}
              {/^dev/i.test(window.location.hostname) && (
                <div className="p-3 bg-amber-50 rounded-xl space-y-2 border border-amber-200">
                  <p className="text-xs text-amber-700 font-medium">Dev Server — currently: {window.location.hostname}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {['dev', 'dev1', 'dev2', 'dev3'].map(sub => {
                      const host = `${sub}.agri-books.com`;
                      const active = window.location.hostname === host;
                      return (
                        <button key={sub}
                          onClick={() => {
                            try { localStorage.setItem('agribooks_dev_server', host); } catch { /* ignore */ }
                            window.location.replace(`https://${host}/terminal`);
                          }}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border ${active ? 'bg-amber-600 text-white border-amber-700' : 'bg-white text-slate-600 border-slate-300'}`}>
                          {sub}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Text-mode printing (H10P built-in font) — tuning controls.
                  Disabled by default after the 2026-07-13 on-paper test; the
                  calibration strip reveals the SDK's real table/QR semantics. */}
              {PrintBridge.isNative() && (
                <div className="p-3 bg-slate-50 rounded-xl space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Text-Mode Printing (beta)</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">Printer's built-in font · under tuning</p>
                    </div>
                    <button
                      onClick={() => {
                        const cur = PrintBridge.getTextModeSetting();
                        const next = cur === 'on' ? 'off' : 'on';
                        PrintBridge.setTextModeSetting(next);
                        toast.success(`Text mode: ${next.toUpperCase()} (this device)`);
                        setSettingsOpen(false);
                      }}
                      data-testid="toggle-textmode-btn"
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${PrintBridge.getTextModeSetting() === 'on' ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                    >
                      {PrintBridge.getTextModeSetting() === 'on' ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await PrintBridge.printCalibration();
                        toast.success('Calibration strip sent to printer');
                      } catch (e) {
                        toast.error(e?.message || 'Calibration print failed');
                      }
                    }}
                    data-testid="print-calibration-btn"
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors text-xs font-semibold"
                  >
                    <Printer size={14} />
                    Print calibration strip
                  </button>
                </div>
              )}

              <button onClick={() => { setSettingsOpen(false); handleLogout(); }}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-colors text-sm font-medium"
                data-testid="unlink-terminal-btn">
                <Unlink size={16} />
                Unlink Terminal
              </button>              <button onClick={() => setSettingsOpen(false)}
                className="w-full py-2.5 text-sm text-slate-500 hover:text-slate-700 transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── QR Camera Scanner Overlay ── */}
      {qrScannerOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-black" data-testid="qr-scanner-overlay">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-black/80 shrink-0">
            <div className="flex items-center gap-2.5">
              <ScanLine size={18} className="text-purple-400" />
              <div>
                <p className="text-sm font-semibold text-white">Scan Document QR</p>
                <p className="text-[10px] text-slate-400">
                  {qrCameraActive ? 'Point camera at receipt or document QR code' : 'Hardware scanner active'}
                </p>
              </div>
            </div>
            <button
              onClick={stopQrScanner}
              className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              data-testid="qr-scanner-close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 relative overflow-hidden">
            {qrCameraActive ? (
              /* Camera view */
              <div id="terminal-qr-scanner-view" className="w-full h-full" />
            ) : (
              /* Idle state — hardware scanner ready, camera off */
              <div className="flex flex-col items-center justify-center h-full px-8 space-y-8"
                   data-testid="qr-scanner-idle">
                {/* Animated scan frame */}
                <div className="relative w-52 h-52">
                  {/* Corner brackets */}
                  <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-emerald-400 rounded-tl-lg" />
                  <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-emerald-400 rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-emerald-400 rounded-bl-lg" />
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-emerald-400 rounded-br-lg" />
                  {/* Scan line animation */}
                  <div className="absolute inset-x-4 top-4 h-0.5 bg-emerald-400/70 animate-scanline" />
                  {/* Center icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <ScanLine size={40} className="text-white/20" />
                  </div>
                </div>

                <div className="text-center space-y-1.5">
                  <p className="text-white font-semibold text-base">Scan the QR Code</p>
                  <p className="text-slate-400 text-xs">Use the device's built-in scanner</p>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 w-full max-w-xs">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-slate-500 text-xs">or</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>

                {/* Use Camera button */}
                <button
                  onClick={startCamera}
                  className="flex items-center gap-2.5 px-6 py-3 rounded-2xl border border-purple-500/40 bg-purple-900/30 text-purple-300 font-semibold text-sm active:scale-95 transition-all hover:bg-purple-900/50"
                  data-testid="use-camera-btn"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  Use Camera
                </button>
              </div>
            )}
          </div>

          {/* Hardware scanner ready strip — always shown */}
          <div className="bg-black/90 border-t border-white/10 px-5 py-3.5 flex items-center gap-3 shrink-0"
               data-testid="hw-scanner-ready-strip">
            <div className="relative shrink-0">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
              <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping opacity-60" />
            </div>
            <p className="text-xs text-slate-300">
              <span className="font-semibold text-white">Hardware scanner active</span>
              {' '}— scan any receipt QR code to open it instantly
            </p>
          </div>
        </div>
      )}

      {/* ── QuickScan Sheet — shown when hardware scanner reads a doc QR code ── */}
      {quickScanDoc && (
        <div className="fixed inset-0 z-[60] flex flex-col justify-end" style={{ pointerEvents: 'auto' }} data-testid="quickscan-sheet">
          {/* z-[60] + pointerEvents:auto — layer audit 2026-09-01: the
              hardware scanner can pop this while a checkout dialog is open;
              without these the sheet paints behind and eats no clicks. */}
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" onClick={() => setQuickScanDoc(null)} />
          {/* Sheet */}
          <div className="relative bg-white rounded-t-3xl overflow-hidden" style={{boxShadow:'0 -4px 24px rgba(0,0,0,0.13)'}}>
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-slate-300" />
            </div>

            {quickScanDoc.loading ? (
              <div className="px-5 py-8 flex flex-col items-center gap-3">
                <RefreshCw size={24} className="animate-spin text-emerald-500" />
                <p className="text-sm text-slate-500">Loading document...</p>
                <p className="text-xs text-slate-400">{quickScanDoc.code}</p>
              </div>
            ) : quickScanDoc.basic ? (
              <div className="px-5 pb-6 space-y-4">
                {/* Doc header */}
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                      {QUICKSCAN_DOC_LABELS[quickScanDoc.basic.doc_type] || 'Branch Transfer'}
                    </p>
                    <p className="text-xl font-bold text-slate-900 mt-0.5" data-testid="quickscan-doc-number">
                      {quickScanDoc.basic.number}
                    </p>
                    <p className="text-sm text-slate-500 mt-0.5">
                      {quickScanDoc.basic.doc_type === 'stock_request'
                        ? `${quickScanDoc.basic.requesting_branch_name || '—'} requests from ${quickScanDoc.basic.supplying_branch_name || '—'}`
                        : (quickScanDoc.basic.customer_name || quickScanDoc.basic.supplier_name
                          || `${quickScanDoc.basic.from_branch} → ${quickScanDoc.basic.to_branch}`)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-[#1A4D2E]" data-testid="quickscan-doc-amount">
                      {quickScanDoc.basic.doc_type === 'stock_request'
                        ? <>{Math.round((quickScanDoc.basic.total_qty || 0) * 100) / 100} <span className="text-xs font-medium text-slate-500">qty</span></>
                        : fmtPHP(quickScanDoc.basic.grand_total || quickScanDoc.basic.total || 0)}
                    </p>
                    {quickScanDoc.basic.balance > 0 && (
                      <p className="text-xs text-red-500 font-semibold">
                        Balance: {fmtPHP(quickScanDoc.basic.balance)}
                      </p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-0.5">{quickScanDoc.basic.branch_name}</p>
                  </div>
                </div>

                {/* Status + item count */}
                <div className="flex items-center gap-2 text-xs">
                  <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-medium">
                    {quickScanDoc.basic.status}
                  </span>
                  <span className="text-slate-400">
                    {quickScanDoc.basic.items?.length || 0} item(s)
                  </span>
                  <span className="text-slate-300">·</span>
                  <span className="text-slate-400 text-[10px]">{quickScanDoc.code}</span>
                </div>

                {/* Action buttons */}
                <div className="space-y-2" data-testid="quickscan-actions">
                  <button
                    onClick={() => {
                      navigate(`/doc/${quickScanDoc.code}?branch=${session.branchId}&device=${session.deviceId || ''}`);
                      setQuickScanDoc(null);
                    }}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-[#1A4D2E] text-white font-semibold text-sm active:scale-95 transition-transform"
                    data-testid="quickscan-view-doc"
                  >
                    <ExternalLink size={15} /> View & Reprint
                  </button>

                  {/* Stock request — editable ONLY from the supplying branch's own
                      terminal while awaiting triage (owner rule 2026-09-03: who +
                      where decide; the backend enforces tenant + terminal branch).
                      Everyone else sees where it's going, view-only. */}
                  {quickScanDoc.basic.doc_type === 'stock_request' && (
                    quickScanDoc.basic.raw_status === 'sent' &&
                    quickScanDoc.basic.supplying_branch_id === session.branchId ? (
                      <button
                        onClick={() => {
                          // Reuse the /doc terminal tunnel: the doc page reads the paired
                          // session + ?branch= and shows the triage panel behind the PIN.
                          navigate(`/doc/${quickScanDoc.code}?branch=${session.branchId}&device=${session.deviceId || ''}&action=triage`);
                          setQuickScanDoc(null);
                        }}
                        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-teal-600 text-white font-semibold text-sm active:scale-95 transition-transform"
                        data-testid="quickscan-triage"
                      >
                        <ClipboardCheck size={15} /> Triage this request
                      </button>
                    ) : (
                      <p className="text-[11px] text-slate-500 text-center px-2" data-testid="quickscan-sr-viewonly">
                        {quickScanDoc.basic.raw_status === 'sent'
                          ? <>View only — to be fulfilled by <span className="font-semibold">{quickScanDoc.basic.supplying_branch_name || 'another branch'}</span>. Triage from that branch&apos;s terminal.</>
                          : <>View only — {String(quickScanDoc.basic.status || '').toLowerCase()}.</>}
                      </p>
                    )
                  )}

                  {/* Quick Collect Payment — only for credit invoices with balance */}
                  {quickScanDoc.basic.doc_type === 'invoice' &&
                   parseFloat(quickScanDoc.basic.balance || 0) > 0 &&
                   quickScanDoc.basic.customer_id && (
                    <button
                      onClick={() => {
                        // Navigate to the doc page which already has PIN-gated payment panel
                        navigate(`/doc/${quickScanDoc.code}?branch=${session.branchId}&device=${session.deviceId || ''}&action=payment`);
                        setQuickScanDoc(null);
                      }}
                      className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-emerald-600 text-white font-semibold text-sm active:scale-95 transition-transform"
                      data-testid="quickscan-collect-payment"
                    >
                      <Wallet size={15} />
                      Collect Payment — {fmtPHP(quickScanDoc.basic.balance)} due
                    </button>
                  )}
                  <button
                    onClick={() => setQuickScanCloudPrint(true)}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-emerald-200 text-emerald-700 bg-emerald-50 font-semibold text-sm active:scale-95 transition-transform"
                    data-testid="quickscan-cloud-print"
                  >
                    <Printer size={15} /> Send to Cloud Printer
                  </button>
                  <button
                    onClick={() => setQuickScanDoc(null)}
                    className="w-full py-2.5 text-sm text-slate-400 hover:text-slate-600 transition-colors"
                    data-testid="quickscan-close"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : quickScanDoc.notFound ? (
              /* ── Not found state — stays in-overlay, never navigates away ── */
              <div className="px-5 py-8 text-center space-y-4" data-testid="quickscan-not-found">
                <div className="w-14 h-14 rounded-full bg-amber-50 border-2 border-amber-200 flex items-center justify-center mx-auto">
                  <AlertTriangle size={24} className="text-amber-500" />
                </div>
                <div>
                  <p className="text-base font-bold text-slate-800">No Document Found</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Code <span className="font-mono font-semibold text-slate-600">{quickScanDoc.code}</span> does not match any document in this system.
                  </p>
                </div>
                <div className="space-y-2 pt-1">
                  <button
                    onClick={() => { setQuickScanDoc(null); startQrScanner(); }}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-[#1A4D2E] text-white font-semibold text-sm active:scale-95 transition-transform"
                    data-testid="quickscan-try-again-btn"
                  >
                    <ScanLine size={15} /> Scan Again
                  </button>
                  <button
                    onClick={() => setQuickScanDoc(null)}
                    className="w-full py-2.5 text-sm text-slate-400 hover:text-slate-600 transition-colors"
                    data-testid="quickscan-not-found-close"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              /* ── Empty / unexpected state ── */
              <div className="px-5 py-8 text-center space-y-3">
                <CheckCircle2 size={28} className="text-slate-300 mx-auto" />
                <p className="text-sm text-slate-500">Document not found</p>
                <button onClick={() => setQuickScanDoc(null)} className="text-xs text-slate-400 hover:underline">Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Document Upload Overlay */}
      {docUploadOpen && (
        <TerminalDocUpload
          branchId={session.branchId}
          onClose={() => setDocUploadOpen(false)}
        />
      )}

      {/* Collect Payment Overlay — PIN gated, same rules as /payments */}
      {collectPaymentOpen && (
        <TerminalCollectPayment
          api={api}
          session={session}
          businessInfo={businessInfo}
          prefillCustomer={customerAccountPrefill}
          onClose={() => { setCollectPaymentOpen(false); setCustomerAccountPrefill(null); }}
        />
      )}

      {/* Customer Account Overlay — PIN gated, search + account view + print */}
      {customerAccountOpen && (
        <TerminalCustomerAccount
          api={api}
          session={session}
          businessInfo={businessInfo}
          onClose={() => { setCustomerAccountOpen(false); setCustomerAccountPrefill(null); }}
          openCollectPayment={(customer) => {
            setCustomerAccountPrefill(customer);
            setCollectPaymentOpen(true);
          }}
        />
      )}

      {/* POS History Overlay — Sales + Purchase history with PIN gate.
          Row clicks navigate to /doc/{code} which is the same modal the
          terminal's search opens, so reprint UX matches end-to-end. */}
      <TerminalHistoryOverlay
        api={api}
        open={historyOverlayOpen}
        onClose={() => setHistoryOverlayOpen(false)}
        branch={{ id: session.branchId, name: session.branchName || session.branch_name || '' }}
        isOnline={isOnline}
        pinSession={posHistoryPinSession}
        startPinSession={startHistoryPinSession}
        onActivity={refreshHistorySession}
      />

      {/* QuickScan Send to Print — shared modal (print stations + terminals) */}
      {quickScanDoc?.basic && (
        <SendToPrintModal
          open={quickScanCloudPrint}
          onOpenChange={setQuickScanCloudPrint}
          compact={true}
          axiosInstance={api}
          documentType={quickScanDoc.basic.doc_type === 'invoice' ? 'sales_receipt' : (QUICKSCAN_PRINT_TYPE[quickScanDoc.basic.doc_type] || 'branch_transfer')}
          receiverDocType={['invoice', 'purchase_order', 'branch_transfer', 'stock_request'].includes(quickScanDoc.basic.doc_type) ? quickScanDoc.basic.doc_type : ''}
          documentName={`${QUICKSCAN_DOC_LABELS[quickScanDoc.basic.doc_type] || 'Document'} #${quickScanDoc.basic.number || ''}`}
          documentId={quickScanDoc.basic.doc_id || quickScanDoc.basic.id || ''}
          docCode={quickScanDoc.code || ''}
          referenceNumber={quickScanDoc.basic.number || ''}
          branchId={session.branchId}
          buildHtml={(format) => {
            try {
              const data = basicDocToPrintData(quickScanDoc.basic);
              const type = QUICKSCAN_PRINT_TYPE[quickScanDoc.basic.doc_type] || 'branch_transfer';
              return PrintEngine.generateHtml({ type, data, format, businessInfo: businessInfo || {}, docCode: '' });
            } catch { return '<p>Document</p>'; }
          }}
          metadata={{ doc_type: quickScanDoc.basic.doc_type }}
        />
      )}

      {/* Print Queue Overlay */}      {printQueueOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col justify-end" style={{ pointerEvents: 'auto' }} data-testid="print-queue-overlay">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPrintQueueOpen(false)} />
          <div className="relative bg-white rounded-t-3xl overflow-hidden max-h-[70vh]" style={{ boxShadow: '0 -4px 24px rgba(0,0,0,0.13)' }}>
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-slate-300" />
            </div>
            <div className="px-5 pb-2 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  <Printer size={15} className="inline mr-1.5 text-emerald-600" />
                  Print Queue
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">Mode: <b className="capitalize">{printMode}</b></p>
              </div>
              <button onClick={() => setPrintQueueOpen(false)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <X size={15} />
              </button>
            </div>
            <div className="overflow-y-auto px-5 pb-6 space-y-2">
              {printQueue.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Printer size={28} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No pending print jobs</p>
                </div>
              ) : (
                printQueue.map(job => (
                  <div key={job.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200" data-testid={`print-queue-item-${job.id}`}>
                    <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                      <Printer size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{job.document_name || 'Document'}</p>
                      <p className="text-[10px] text-slate-400">{job.document_type?.replace(/_/g, ' ')}{job.copies > 1 ? ` · ${job.copies} copies` : ''}</p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button
                        onClick={() => { printClaimedJob(job); setPrintQueueOpen(false); }}
                        className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 active:scale-95 transition-all"
                        data-testid={`print-now-${job.id}`}
                      >
                        <Printer size={11} className="inline mr-1" />Print
                      </button>
                      <button
                        onClick={() => {
                          const device = printDeviceRef.current;
                          if (device?.token) jobFailed(device.token, job.id, 'Dismissed on the terminal').catch(() => {});
                          setPrintQueue(prev => prev.filter(j => j.id !== job.id));
                        }}
                        className="px-2.5 py-1.5 rounded-lg bg-slate-200 text-slate-500 text-xs hover:bg-red-50 hover:text-red-600 transition-colors"
                      >
                        <X size={11} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

